import { StageContractUploadResponseSchema, PaymentEvidenceUploadResponseSchema, FeeAdjustmentWriteResponseSchema, LifecycleCheckSchema,
  ContentProgressDashboardSchema, ContentProgressProjectDetailSchema,
  OperationalClaimListSchema, ProjectFinanceSchema,
  OperationalClaimWriteResponseSchema, PendingSettlementPoolSchema, PersonalSettlementDetailSchema,
  PersonalSettlementListSchema, PersonalSettlementWriteResponseSchema, PublicationPlanListSchema,
  PublicationDossierSchema, PublicationPlanWriteResponseSchema, PublicationWorkbenchSchema,
  PublicationWorkspaceSchema, SettlementExportTicketSchema, WorkspaceDashboardSchema, ManagementDashboardSchema,
  ManagementDashboardPersonDetailSchema, PersonalOutputDashboardSchema, PersonalOutputProjectDetailSchema,
  type ManagementDashboardQuery,
  type ContentProgressQuery,
  type PersonalOutputQuery,
  type ConfirmFeeAdjustmentRequest, type GeneratePersonalSettlementRequest, type IssueSettlementExportRequest,
  type PersonalSettlementListFilter,
  type PublicationAttemptRequest,
  type RecordPersonalPaymentRequest,
  type RecordDirectClaimPaymentRequest,
  type ReviewOperationalClaimRequest, type SavePublicationPlanRequest, type SubmitOperationalClaimRequest,
  type PublicationVideoUploadResponse } from '@youkuai/contracts';

import { apiDownload, apiRequest } from './project-api.ts';
import { PublicationVideoUploadResponseSchema } from '@youkuai/contracts';

function write(key: string, body: unknown, method = 'POST'): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key }, body: JSON.stringify(body) };
}
export function stageEvidence(input: { token: string; projectId: string; key: string; file: File }) {
  const query = new URLSearchParams({ filename: input.file.name });
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/evidence-uploads?${query}`, input.token,
    { method: 'POST', headers: { 'Content-Type': input.file.type, 'Idempotency-Key': input.key }, body: input.file })
    .then((value) => StageContractUploadResponseSchema.parse(value));
}
export function stagePaymentEvidence(input: { token: string; key: string; file: File }) {
  const query = new URLSearchParams({ filename: input.file.name });
  return apiRequest(`/finance/evidence-uploads?${query}`, input.token,
    { method: 'POST', headers: { 'Content-Type': input.file.type, 'Idempotency-Key': input.key }, body: input.file })
    .then((value) => PaymentEvidenceUploadResponseSchema.parse(value));
}
export function listPublicationPlans(token: string, projectId: string) {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/publication-plans`, token)
    .then((value) => PublicationPlanListSchema.parse(value));
}
export function getPublicationWorkbench(token: string, projectId: string,
  filter: { status?: string; packageId?: string; query?: string } = {}) {
  const params = new URLSearchParams();
  if (filter.status && filter.status !== 'ALL') params.set('status', filter.status);
  if (filter.packageId && filter.packageId !== 'ALL') params.set('packageId', filter.packageId);
  if (filter.query && filter.query.trim()) params.set('q', filter.query.trim());
  const suffix = params.size ? `?${params}` : '';
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/publications/workbench${suffix}`, token)
    .then((value) => PublicationWorkbenchSchema.parse(value));
}
export function getPublicationVideoTicket(input: { token: string; projectId: string; planId: string;
  videoId: string }): Promise<{ ticket: string; expiresAt: string }> {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/publication-plans/` +
    `${encodeURIComponent(input.planId)}/videos/${encodeURIComponent(input.videoId)}/download-ticket`,
    input.token, { method: 'POST' }).then((value) => value as { ticket: string; expiresAt: string });
}
export function publicationVideoDownloadUrl(input: { projectId: string; planId: string; videoId: string;
  ticket: string }): string {
  return `/projects/${encodeURIComponent(input.projectId)}/publication-plans/${encodeURIComponent(input.planId)}` +
    `/videos/${encodeURIComponent(input.videoId)}/download?ticket=${encodeURIComponent(input.ticket)}`;
}
export function getPublicationDossier(token: string, projectId: string, contentId: string) {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/contents/${encodeURIComponent(contentId)}/publication-dossier`,
    token).then((value) => PublicationDossierSchema.parse(value));
}
export function getPublicationWorkspace(token: string, projectId: string) {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/publications/workspace`, token)
    .then((value) => PublicationWorkspaceSchema.parse(value));
}
export function savePublicationPlan(input: { token: string; projectId: string; contentId: string; key: string;
  request: SavePublicationPlanRequest }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/contents/${encodeURIComponent(input.contentId)}/publication-plan`,
    input.token, write(input.key, input.request, 'PUT')).then((value) => PublicationPlanWriteResponseSchema.parse(value));
}
export function recordPublicationAttempt(input: { token: string; projectId: string; planId: string; key: string;
  request: PublicationAttemptRequest }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/publication-plans/${encodeURIComponent(input.planId)}/attempts`,
    input.token, write(input.key, input.request)).then((value) => PublicationPlanWriteResponseSchema.parse(value));
}
export function listOperationalClaims(token: string, projectId: string, packageId?: string) {
  const query = packageId ? `?packageId=${encodeURIComponent(packageId)}` : '';
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/work-expenses${query}`, token)
    .then((value) => OperationalClaimListSchema.parse(value));
}
export function submitOperationalClaim(input: { token: string; projectId: string; key: string;
  request: SubmitOperationalClaimRequest }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/work-expenses`, input.token,
    write(input.key, input.request)).then((value) => OperationalClaimWriteResponseSchema.parse(value));
}
export function reviewOperationalClaim(input: { token: string; projectId: string; claimId: string; key: string;
  request: ReviewOperationalClaimRequest }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/work-expenses/${encodeURIComponent(input.claimId)}/review`,
    input.token, write(input.key, input.request)).then((value) => OperationalClaimWriteResponseSchema.parse(value));
}
export function payOperationalClaim(input: { token: string; projectId: string; claimId: string; key: string;
  request: RecordDirectClaimPaymentRequest }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/work-expenses/${encodeURIComponent(input.claimId)}/direct-payment`,
    input.token, write(input.key, input.request)).then((value) => OperationalClaimWriteResponseSchema.parse(value));
}
export function getProjectFinance(token: string, projectId: string) {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/finance`, token)
    .then((value) => ProjectFinanceSchema.parse(value));
}
export function confirmFeeAdjustment(input: { token: string; projectId: string; adjustmentId: string; key: string;
  request: ConfirmFeeAdjustmentRequest }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/fee-adjustments/` +
    `${encodeURIComponent(input.adjustmentId)}/confirm`, input.token, write(input.key, input.request))
    .then((value) => FeeAdjustmentWriteResponseSchema.parse(value));
}
export function getPendingSettlementPool(token: string, from: string, until: string, personId?: string,
  engagementTypes: Array<'EMPLOYEE' | 'LONG_TERM_EXECUTOR'> = ['EMPLOYEE', 'LONG_TERM_EXECUTOR']) {
  const query = new URLSearchParams({ from, until, engagementTypes: engagementTypes.join(',') });
  if (personId) query.set('personId', personId);
  return apiRequest(`/finance/pending-settlements?${query}`, token).then((value) => PendingSettlementPoolSchema.parse(value));
}
export function listPersonalSettlements(token: string, filter: PersonalSettlementListFilter) {
  const query = new URLSearchParams({ from: filter.periodFrom, until: filter.periodUntil,
    engagementTypes: filter.engagementTypes.join(','), statuses: filter.statuses.join(','),
    search: filter.search, sortDirection: filter.sortDirection });
  if (filter.paidFrom) query.set('paidFrom', filter.paidFrom);
  if (filter.paidUntil) query.set('paidUntil', filter.paidUntil);
  if (filter.generatedFrom) query.set('generatedFrom', filter.generatedFrom);
  if (filter.generatedUntil) query.set('generatedUntil', filter.generatedUntil);
  return apiRequest(`/finance/personal-settlements?${query}`, token)
    .then((value) => PersonalSettlementListSchema.parse(value));
}
export function getPersonalSettlement(token: string, id: string) {
  return apiRequest(`/finance/personal-settlements/${encodeURIComponent(id)}`, token)
    .then((value) => PersonalSettlementDetailSchema.parse(value));
}
export function generatePersonalSettlement(input: { token: string; key: string; request: GeneratePersonalSettlementRequest }) {
  return apiRequest('/finance/personal-settlements', input.token, write(input.key, input.request))
    .then((value) => PersonalSettlementWriteResponseSchema.parse(value));
}
export function voidPersonalSettlement(input: { token: string; statementId: string; key: string;
  expectedVersion: number; reason: string }) {
  return apiRequest(`/finance/personal-settlements/${encodeURIComponent(input.statementId)}/void`, input.token,
    write(input.key, { expectedVersion: input.expectedVersion, reason: input.reason }))
    .then((value) => PersonalSettlementWriteResponseSchema.parse(value));
}
export function recordPersonalPayment(input: { token: string; statementId: string; key: string;
  request: RecordPersonalPaymentRequest }) {
  return apiRequest(`/finance/personal-settlements/${encodeURIComponent(input.statementId)}/payments`, input.token,
    write(input.key, input.request)).then((value) => PersonalSettlementWriteResponseSchema.parse(value));
}
export async function exportSettlementDetails(input: { token: string; key: string;
  request: IssueSettlementExportRequest }): Promise<{ file: Blob; ticketId: string; rowCount: number }> {
  const issued = SettlementExportTicketSchema.parse(await apiRequest('/finance/settlement-exports', input.token,
    write(input.key, input.request)));
  const file = await apiDownload(`/finance/settlement-exports/${encodeURIComponent(issued.ticketId)}`, input.token);
  return { file, ticketId: issued.ticketId, rowCount: issued.rowCount };
}
export function getPackageCompletionCheck(token: string, projectId: string, packageId: string) {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/service-packages/${encodeURIComponent(packageId)}/completion-check`, token)
    .then((value) => LifecycleCheckSchema.parse(value));
}
export function getProjectArchiveCheck(token: string, projectId: string) {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/archive-check`, token)
    .then((value) => LifecycleCheckSchema.parse(value));
}
export function archiveProjectV2(input: { token: string; projectId: string; key: string; expectedVersion: number;
  actualCompletedOn: string; note: string }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/archive`, input.token,
    write(input.key, { expectedVersion: input.expectedVersion, actualCompletedOn: input.actualCompletedOn, note: input.note }));
}
export function reopenProjectV2(input: { token: string; projectId: string; key: string; expectedVersion: number; reason: string }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/reopen`, input.token,
    write(input.key, { expectedVersion: input.expectedVersion, reason: input.reason }));
}
export function getWorkspaceDashboard(token: string) {
  return apiRequest('/workspace/dashboard', token).then((value) => WorkspaceDashboardSchema.parse(value));
}
function contentProgressQuery(filter: ContentProgressQuery): URLSearchParams {
  const query = new URLSearchParams({ from: filter.from, until: filter.until });
  if (filter.contentType) query.set('contentType', filter.contentType);
  if (filter.projectId) query.set('projectId', filter.projectId);
  if (filter.taskCode) query.set('taskCode', filter.taskCode);
  return query;
}
export function getContentProgress(token: string, filter: ContentProgressQuery) {
  return apiRequest(`/workspace/content-progress?${contentProgressQuery(filter)}`, token)
    .then((value) => ContentProgressDashboardSchema.parse(value));
}
export function getContentProgressProject(token: string, projectId: string, filter: ContentProgressQuery) {
  return apiRequest(`/workspace/content-progress/projects/${encodeURIComponent(projectId)}?${contentProgressQuery(filter)}`, token)
    .then((value) => ContentProgressProjectDetailSchema.parse(value));
}
function personalOutputQuery(filter: PersonalOutputQuery): URLSearchParams {
  const query = new URLSearchParams({ from: filter.from, until: filter.until });
  if (filter.contentType) query.set('contentType', filter.contentType);
  if (filter.taskCode) query.set('taskCode', filter.taskCode);
  if (filter.assignmentRole) query.set('assignmentRole', filter.assignmentRole);
  return query;
}
export function getPersonalOutput(token: string, filter: PersonalOutputQuery) {
  return apiRequest(`/workspace/personal-output?${personalOutputQuery(filter)}`, token)
    .then((value) => PersonalOutputDashboardSchema.parse(value));
}
export function getPersonalOutputProject(token: string, projectId: string, filter: PersonalOutputQuery) {
  return apiRequest(`/workspace/personal-output/projects/${encodeURIComponent(projectId)}?${personalOutputQuery(filter)}`, token)
    .then((value) => PersonalOutputProjectDetailSchema.parse(value));
}
function managementDashboardQuery(filter: ManagementDashboardQuery): URLSearchParams {
  const query = new URLSearchParams({ from: filter.from, until: filter.until });
  if (filter.managerId) query.set('managerId', filter.managerId);
  if (filter.businessEntityId) query.set('businessEntityId', filter.businessEntityId);
  if (filter.projectStatus) query.set('projectStatus', filter.projectStatus);
  if (filter.duty) query.set('duty', filter.duty);
  if (filter.engagementTypes.length) query.set('engagementTypes', filter.engagementTypes.join(','));
  if (filter.personIds.length) query.set('personIds', filter.personIds.join(','));
  return query;
}
export function getManagementDashboard(token: string, filter: ManagementDashboardQuery) {
  return apiRequest(`/management-dashboard?${managementDashboardQuery(filter)}`, token)
    .then((value) => ManagementDashboardSchema.parse(value));
}
export function exportManagementDashboard(token: string, filter: ManagementDashboardQuery) {
  return apiDownload(`/management-dashboard/export?${managementDashboardQuery(filter)}`, token);
}
export function getManagementDashboardPerson(token: string, personId: string, filter: ManagementDashboardQuery) {
  return apiRequest(`/management-dashboard/people/${encodeURIComponent(personId)}?${managementDashboardQuery(filter)}`, token)
    .then((value) => ManagementDashboardPersonDetailSchema.parse(value));
}

/** BUG-0129：发布计划成片上传（上限见 contracts 的 PUBLICATION_VIDEO_MAX_BYTES，不限格式）。 */
export function stagePublicationVideo(input: { token: string; projectId: string; key: string; file: File }) {
  const query = new URLSearchParams({ filename: input.file.name });
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/publication-video-uploads?${query}`, input.token,
    { method: 'POST', headers: { 'Content-Type': input.file.type || 'application/octet-stream',
      'Idempotency-Key': input.key }, body: input.file })
    .then((value) => PublicationVideoUploadResponseSchema.parse(value) as PublicationVideoUploadResponse);
}
export function attachPublicationVideo(input: { token: string; projectId: string; planId: string; key: string;
  stagingId: string }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/publication-plans/` +
    `${encodeURIComponent(input.planId)}/videos`, input.token,
    write(input.key, { stagingId: input.stagingId })).then((value) => PublicationPlanWriteResponseSchema.parse(value));
}
export function removePublicationVideo(input: { token: string; projectId: string; planId: string; videoId: string; key: string }) {
  return apiRequest(`/projects/${encodeURIComponent(input.projectId)}/publication-plans/` +
    `${encodeURIComponent(input.planId)}/videos/${encodeURIComponent(input.videoId)}/remove`, input.token,
    write(input.key, {})).then((value) => PublicationPlanWriteResponseSchema.parse(value));
}
